import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { X } from 'lucide-react';

/**
 * Dialog - Componente de diálogo modal reutilizable
 * Basado en el diseño del Modal de contactos para consistencia
 */
export const Dialog = ({ 
  isOpen, 
  onClose, 
  title, 
  subtitle = "",
  children, 
  size = 'xl',
  isDarkMode = false,
  closeOnOverlayClick = false,
  closeOnEscape = true,
  style = {},
  /** Por encima de drawers/paneles (ej. formulario cotización z-index 10000). */
  zIndex = 9999,
}) => {
  const theme = useTheme();

  // Tamaños del modal (consistentes con Modal.js)
  const sizeStyles = {
    sm: { width: '400px', maxWidth: '90vw' },
    md: { width: '600px', maxWidth: '90vw' },
    lg: { width: '800px', maxWidth: '95vw' },
    xl: { width: '1000px', maxWidth: '95vw' },
    xxl: { width: '1200px', maxWidth: '95vw' }
  };

  // Estilos del modal
  const overlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: isOpen ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex,
    padding: theme.spacing.md
  };

  const modalStyles = {
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.lg,
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    ...sizeStyles[size],
    ...style
  };

  const headerStyles = {
    padding: theme.spacing.lg,
    borderBottom: `1px solid ${theme.colors.border.primary}`,
    backgroundColor: theme.colors.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0
  };

  const titleStyles = {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.text.primary,
    margin: 0
  };

  const subtitleStyles = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.text.secondary,
    margin: '4px 0 0 0'
  };

  const closeButtonStyles = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
    color: theme.colors.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease-in-out'
  };

  const contentStyles = {
    padding: theme.spacing.lg,
    overflowY: 'auto',
    flex: 1
  };

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Agregar event listener para ESC cuando el modal está abierto
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (closeOnEscape && e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      if (closeOnEscape) {
        document.addEventListener('keydown', handleKeyDown);
      }
      document.body.style.overflow = 'hidden'; // Prevenir scroll del body
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      if (closeOnEscape) {
        document.removeEventListener('keydown', handleKeyDown);
      }
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, closeOnEscape, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      style={overlayStyles} 
      onClick={handleOverlayClick}
    >
      <div style={modalStyles}>
        {/* Header del Modal */}
        <div style={headerStyles}>
          <div>
            <h2 style={titleStyles}>{title}</h2>
            {subtitle && <p style={subtitleStyles}>{subtitle}</p>}
          </div>
          <button
            style={closeButtonStyles}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.hover;
              e.target.style.color = theme.colors.text.primary;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = theme.colors.text.secondary;
            }}
            aria-label="Cerrar modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido del Modal */}
        <div style={contentStyles}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Dialog;
